import type { ConditionFieldType, Operator } from "replo-runtime/shared/enums";
import type {
  ConditionStatement,
  Option,
  StoreProduct,
} from "replo-runtime/shared/types";
import type { ConditionField } from "schemas/generated/symbol";
import type { ProductRefOrDynamic } from "schemas/product";

import * as React from "react";

import { selectLocaleData } from "@v1/reducers/commerce-reducer";
import { selectTemplateEditorStoreProduct } from "@v1/reducers/template-reducer";
import { useEditorSelector } from "@v1/store/store";
import { getProduct, isContextRef } from "replo-runtime/store/ReploProduct";

import { getConditionFieldDisplayName } from "./conditionFieldToDisplayName";

type ConditionEditorDataFormatExtras = {
  products: StoreProduct[];
};
interface ConditionEditorData {
  fieldType: ConditionFieldType;
  displayName: string;
  /* Populated later */
  operatorOptions: Option[];
  editorConfig: ConditionFieldEditorValueConfig;
  defaultValue?: string | number;
  isValid?(value: ConditionStatement): boolean;
  formatDisplayValue?(
    value: any,
    extras: ConditionEditorDataFormatExtras,
  ): React.ReactNode;
}

interface OperatorEditorData {
  displayName: string;
  shortName: string;
}

const operatorToEditorData: Record<Operator, OperatorEditorData> = {
  excludes: {
    displayName: "excludes",
    shortName: "excludes",
  },
  includes: {
    displayName: "includes",
    shortName: "in",
  },
  neq: {
    displayName: "not equals",
    shortName: "≠",
  },
  eq: {
    displayName: "equals",
    shortName: "=",
  },
  gt: {
    displayName: "greater than",
    shortName: ">",
  },
  gte: {
    displayName: "greater than/equal to",
    shortName: "≥",
  },
  lt: {
    displayName: "less than",
    shortName: "<",
  },
  lte: {
    displayName: "less than/equal to",
    shortName: "≤",
  },
  all: {
    displayName: "all",
    shortName: "all",
  },
};

const getConditionOperatorEditorData = (o: Operator): OperatorEditorData => {
  return operatorToEditorData[o];
};

const getOperatorOptions = (operators: Operator[]): any => {
  return operators.map((o: Operator) => {
    const data = getConditionOperatorEditorData(o);
    return {
      label: data.displayName,
      value: o,
    };
  });
};

const OperatorOptions: Record<ConditionFieldType, Option[]> = {
  pixel: getOperatorOptions(["lt", "lte", "gt", "gte", "eq", "neq"]),
  integer: getOperatorOptions(["lt", "lte", "gt", "gte", "eq", "neq"]),
  event: [],
  page: [],
  boolean: [],
  productVariant: getOperatorOptions(["eq", "neq"]),
  hashmark: getOperatorOptions(["eq", "neq"]),
  templateProduct: getOperatorOptions(["eq", "neq"]),
  product: getOperatorOptions(["eq", "neq"]),
};

type ConditionFieldEditorValueConfig =
  | { type: "none" }
  | { type: "productVariant" }
  | { type: "input"; placeholder: string }
  | { type: "inputWithSteps"; placeholder: string }
  | { type: "templateProduct" }
  | { type: "product" };

const EditorValue: Record<ConditionFieldType, ConditionFieldEditorValueConfig> =
  {
    pixel: {
      type: "inputWithSteps",
      placeholder: "0",
    },
    event: { type: "none" },
    boolean: {
      type: "input",
      placeholder: "",
    },
    page: {
      type: "input",
      placeholder: "0",
    },
    hashmark: {
      type: "input",
      placeholder: "#",
    },
    // TODO (Noah, 20201-08-06): We should probably not use Inputs for integers
    integer: {
      type: "input",
      placeholder: "0",
    },
    productVariant: {
      type: "productVariant",
    },
    templateProduct: {
      type: "templateProduct",
    },
    product: {
      type: "product",
    },
  };

function getDefaultOperatorValue(type: ConditionFieldType) {
  switch (type) {
    case "pixel":
      return "50px";
    case "integer":
      return 0;
    default:
      return null;
  }
}

const partialConditionFieldEditorData: Record<
  ConditionField,
  // NOTE: (Kurt, 2024-07-08) We omit the displayName field here and populate it later while constructing
  // `ConditionFieldEditorData`. This is done to prevent a circular import error where
  // the `selectDraftElement_warningThisWillRerenderOnEveryUpdate` selector is refferred to before being
  // initialized. The conditionfieldToDisplayName.tsx file acts as a source of truth for the display names
  // of condition fields.
  Omit<ConditionEditorData, "displayName">
> = {
  "state.temporaryCart.numberOfItems": {
    fieldType: "integer",
    operatorOptions: OperatorOptions.integer,
    defaultValue: getDefaultOperatorValue("integer") ?? undefined,
    editorConfig: EditorValue.integer,
    isValid: (statement) => {
      return (
        statement?.value !== undefined && statement?.operator !== undefined
      );
    },
  },
  "state.temporaryCart.containsVariant": {
    fieldType: "productVariant",
    operatorOptions: OperatorOptions.productVariant,
    defaultValue: getDefaultOperatorValue("productVariant") ?? undefined,
    editorConfig: EditorValue.productVariant,
    isValid: (statement) => {
      return statement?.value !== undefined;
    },
    formatDisplayValue: (value, extras) => {
      if (isContextRef(value)) {
        return <div className="mr-1 text-base font-medium">Dynamic Value</div>;
      }
      return (
        <div className="flex flex-col items-start text-base">
          <div className="mr-1 font-medium">Temporary Cart Contains</div>
          <div className="text-muted text-xs">
            <ProductVariantName productRef={value} products={extras.products} />
          </div>
        </div>
      );
    },
  },
  "element.offsetY": {
    fieldType: "pixel",
    operatorOptions: OperatorOptions.pixel,
    defaultValue: getDefaultOperatorValue("pixel") ?? undefined,
    editorConfig: EditorValue.pixel,
    isValid: (statement) => {
      return (
        statement?.value !== undefined && statement?.operator !== undefined
      );
    },
  },
  "state.product.quantity": {
    fieldType: "integer",
    operatorOptions: OperatorOptions.integer,
    defaultValue: getDefaultOperatorValue("integer") ?? undefined,
    editorConfig: EditorValue.integer,
    isValid: (statement) => {
      return (
        statement?.value !== undefined && statement?.operator !== undefined
      );
    },
  },
  "screen.pageY": {
    fieldType: "pixel",
    operatorOptions: OperatorOptions.pixel,
    defaultValue: getDefaultOperatorValue("pixel") ?? undefined,
    editorConfig: EditorValue.pixel,
    isValid: (statement) => {
      return (
        statement?.value !== undefined &&
        statement?.operator !== undefined &&
        statement?.value > 0
      );
    },
  },
  "interaction.clickEvent": {
    fieldType: "event",
    operatorOptions: OperatorOptions.event,
    editorConfig: EditorValue.event,
  },
  "state.product.noProductVariantSelected": {
    fieldType: "event",
    operatorOptions: OperatorOptions.event,
    editorConfig: EditorValue.event,
    formatDisplayValue: function formatDisplayValue() {
      return (
        <div className="mr-1 text-base font-medium">
          No product variant selected
        </div>
      );
    },
  },
  "page.hashmark": {
    fieldType: "hashmark",
    operatorOptions: OperatorOptions.hashmark,
    defaultValue: getDefaultOperatorValue("hashmark") ?? undefined,
    editorConfig: EditorValue.hashmark,
  },
  "interaction.hover": {
    fieldType: "event",
    operatorOptions: OperatorOptions.event,
    editorConfig: EditorValue.event,
  },
  "state.action.loading": {
    fieldType: "event",
    operatorOptions: OperatorOptions.event,
    editorConfig: EditorValue.event,
  },
  "state.collapsibleV2.isOpen": {
    fieldType: "event",
    operatorOptions: OperatorOptions.event,
    editorConfig: EditorValue.event,
  },
  "state.product.selectedVariant": {
    fieldType: "event",
    operatorOptions: OperatorOptions.event,
    editorConfig: EditorValue.event,
  },
  "state.product.selectedSellingPlan": {
    fieldType: "event",
    operatorOptions: OperatorOptions.event,
    editorConfig: EditorValue.event,
  },
  "state.product.isOptionValueUnavailable": {
    fieldType: "event",
    operatorOptions: OperatorOptions.event,
    editorConfig: EditorValue.event,
    formatDisplayValue: function formatDisplayValue() {
      return (
        <div className="mr-1 text-base font-medium">
          Option In List Is Unavailable
        </div>
      );
    },
  },
  "state.product.isVariantUnavailable": {
    fieldType: "event",
    operatorOptions: OperatorOptions.event,
    editorConfig: EditorValue.event,
    formatDisplayValue: function formatDisplayValue() {
      return (
        <div className="mr-1 text-base font-medium">
          Variant in List Is Unavailable
        </div>
      );
    },
  },
  "state.product.selectedVariantUnavailable": {
    fieldType: "event",
    operatorOptions: OperatorOptions.event,
    editorConfig: EditorValue.event,
    formatDisplayValue: function formatDisplayValue() {
      return (
        <div className="mr-1 text-base font-medium">
          Selected Variant Unavailable
        </div>
      );
    },
  },
  product_variant_is_on_sale: {
    fieldType: "event",
    operatorOptions: OperatorOptions.event,
    editorConfig: EditorValue.event,
    formatDisplayValue: function formatDisplayValue() {
      return (
        <div className="mr-1 text-base font-medium">
          Product Variant Is On Sale
        </div>
      );
    },
  },
  "state.selectionList.isItemSelected": {
    fieldType: "event",
    operatorOptions: OperatorOptions.event,
    editorConfig: EditorValue.event,
  },
  "state.product.currentVariantIsOnSale": {
    fieldType: "event",
    operatorOptions: OperatorOptions.event,
    editorConfig: EditorValue.event,
    formatDisplayValue: function formatDisplayValue() {
      return (
        <div className="mr-1 text-base font-medium">
          Variant In List Is On Sale
        </div>
      );
    },
  },
  "state.product.selectedOptionValues": {
    fieldType: "event",
    operatorOptions: OperatorOptions.event,
    editorConfig: EditorValue.event,
  },
  "state.tabsBlock.isCurrentTab": {
    fieldType: "event",
    operatorOptions: OperatorOptions.event,
    editorConfig: EditorValue.event,
    formatDisplayValue: function formatDisplayValue() {
      return (
        <div className="mr-1 text-base font-medium">This is the active tab</div>
      );
    },
  },
  "state.tabsV2Block.isCurrentTab": {
    fieldType: "event",
    operatorOptions: OperatorOptions.event,
    editorConfig: EditorValue.event,
    formatDisplayValue: function formatDisplayValue() {
      return (
        <div className="mr-1 text-base font-medium">This is the active tab</div>
      );
    },
  },
  "state.group.isCurrentItemActive": {
    fieldType: "event",
    operatorOptions: OperatorOptions.event,
    editorConfig: EditorValue.event,
    formatDisplayValue: function formatDisplayValue() {
      return (
        <div className="mr-1 text-base font-medium">Current item is active</div>
      );
    },
  },
  "state.group.isFirstItemActive": {
    fieldType: "event",
    operatorOptions: OperatorOptions.event,
    editorConfig: EditorValue.event,
    formatDisplayValue: function formatDisplayValue() {
      return (
        <div className="mr-1 text-base font-medium">First item is active</div>
      );
    },
  },
  "state.group.isLastItemActive": {
    fieldType: "event",
    operatorOptions: OperatorOptions.event,
    editorConfig: EditorValue.event,
    formatDisplayValue: function formatDisplayValue() {
      return (
        <div className="mr-1 text-base font-medium">Last item is active</div>
      );
    },
  },
  "state.product.templateProductEquals": {
    fieldType: "templateProduct",
    operatorOptions: OperatorOptions.templateProduct,
    editorConfig: EditorValue.templateProduct,
    formatDisplayValue: function formatDisplayValue() {
      return <div className="mr-1 text-base font-medium">Template Product</div>;
    },
  },
  "state.product.selectedProductEquals": {
    fieldType: "product",
    operatorOptions: OperatorOptions.product,
    editorConfig: EditorValue.product,
    formatDisplayValue: function formatDisplayValue() {
      return <div className="mr-1 text-base font-medium">Template Product</div>;
    },
  },
  "state.product.selectedVariantEquals": {
    fieldType: "productVariant",
    operatorOptions: OperatorOptions.productVariant,
    editorConfig: EditorValue.productVariant,
    isValid: (statement) => {
      return statement?.value !== undefined;
    },
    formatDisplayValue: () => {
      return (
        <div className="flex flex-col items-start text-base">
          <div className="mr-1 font-medium">Selected Variant</div>
        </div>
      );
    },
  },
  "state.beforeAfterSlider.isDragging": {
    fieldType: "event",
    operatorOptions: OperatorOptions.event,
    editorConfig: EditorValue.event,
  },
  "state.tooltip.isOpen": {
    fieldType: "event",
    operatorOptions: OperatorOptions.event,
    editorConfig: EditorValue.event,
  },
};

export const getConditionFieldEditorData = (
  conditionField: ConditionField,
): ConditionEditorData => {
  const conditionFieldEditorData: Record<ConditionField, ConditionEditorData> =
    {} as Record<ConditionField, ConditionEditorData>;

  for (const [field, data] of Object.entries(partialConditionFieldEditorData)) {
    conditionFieldEditorData[field as ConditionField] = {
      ...data,
      displayName: getConditionFieldDisplayName(field as ConditionField),
    };
  }
  const conditionData = conditionFieldEditorData[conditionField];
  return conditionData;
};

export const operationToSymbol: Record<Operator, string> = {
  eq: "=",
  neq: "≠",
  gt: ">",
  lt: "<",
  gte: "≥",
  lte: "≤",
  all: "A",
  includes: "⊃",
  excludes: "!",
};

function ProductVariantName({
  productRef,
  products,
}: {
  productRef: ProductRefOrDynamic | null;
  products: StoreProduct[];
}) {
  const {
    activeCurrency,
    activeLanguage,
    moneyFormat,
    moneyWithCurrencyFormat,
  } = useEditorSelector(selectLocaleData);
  const templateEditorProduct = useEditorSelector(
    selectTemplateEditorStoreProduct,
  );
  return (
    <>
      {
        getProduct(productRef, null, {
          productMetafieldValues: {},
          variantMetafieldValues: {},
          products,
          currencyCode: activeCurrency,
          language: activeLanguage,
          moneyFormat,
          moneyWithCurrencyFormat,
          templateProduct: templateEditorProduct ?? null,
          isEditor: true,
          isShopifyProductsLoading: false,
        })?.variant?.name
      }
    </>
  );
}
